import { installConsoleFilter } from '@/utils/console-filter';
installConsoleFilter();

import '@/singletons';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { createRoot } from 'react-dom/client';

import { bffGetApplicationConfig } from '@repo/sdk/funcs/bffGetApplicationConfig';
import { ApplicationConfig } from '@repo/sdk/models/components';

import { gaiaPublicClient } from '@/client/gaia-public';
import { ErrorMessage } from '@/components/error/error';
import { APP_CONFIG_KEY, LAUNCH_DARKLY_RELAY_PORT } from '@/constants';
import { initialize } from '@/init';
import { Root } from '@/root';
import { queryClient } from '@/singletons';
import { generateInitialContext } from '@/utils/launch-darkly-context';

import './index.css';
import '@xyflow/react/dist/style.css';

// Prefetch app configuration and bootstrap the application
const bootstrapApp = async () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) return;

  const root = createRoot(rootElement);

  try {
    // Fetch application configuration
    const appConfig = await queryClient.fetchQuery({
      queryKey: [APP_CONFIG_KEY],
      queryFn: () =>
        gaiaPublicClient<undefined, ApplicationConfig>({
          gaiaFunc: bffGetApplicationConfig,
        }),
    });

    // Initialize analytics and monitoring
    initialize({
      productionDeployment: appConfig.deployment.isProduction,
      productionUI: import.meta.env.PROD,
      testAutomation: navigator.webdriver,
    });

    // Initialize LaunchDarkly if configured
    if (appConfig.launchDarkly?.clientSideId) {
      try {
        // Build LaunchDarkly options
        const ldOptions: Parameters<typeof asyncWithLDProvider>[0]['options'] = {
          logger: {
            debug: () => {},
            info: () => {},
            warn: (message: string) => console.warn('[LaunchDarkly]', message),
            error: (message: string) => console.error('[LaunchDarkly]', message),
          },
        };

        // Configure relay URLs for AgentProxy mode (preauthentication indicates agent mode)
        // Use window.location.hostname so the browser connects to the relay on the same host
        if (appConfig.preauthentication) {
          const ldRelayUrl = `http://${window.location.hostname}:${LAUNCH_DARKLY_RELAY_PORT}`;
          ldOptions.baseUrl = ldRelayUrl;
          ldOptions.streamUrl = ldRelayUrl;
          ldOptions.eventsUrl = ldRelayUrl;
        }

        const LDProvider = await asyncWithLDProvider({
          clientSideID: appConfig.launchDarkly.clientSideId,
          context: generateInitialContext(),
          options: ldOptions,
          timeout: 5, // 5 seconds for initialization
        });

        // Render the application with LaunchDarkly
        root.render(
          <LDProvider>
            <Root />
          </LDProvider>
        );
      } catch (error) {
        console.error('[LaunchDarkly] Failed to initialize:', error);
        // Render without LaunchDarkly if initialization fails
        root.render(<Root />);
      }
    } else {
      console.warn('[LaunchDarkly] No client ID configured, feature flags will not be available');
      // Render the application without LaunchDarkly
      root.render(<Root />);
    }
  } catch (error) {
    console.error('Failed to bootstrap application:', error);

    // Render error state instead of crashing
    root.render(<ErrorMessage errorMessage={'Failed to bootstrap application'} />);
  }
};

// Start the bootstrap process
bootstrapApp();
